'use client';

import { useState } from 'react';

import { Locale } from '@core/Entities/Locale/Locale.entity';
import { getRoundedReviewRating } from '@core/Utils/Reviews/GetRoundedReviewRating';
import { getSlugFromReviewsAndMakes } from '@core/Utils/Reviews/GetSlugFromReviewsAndMakes';
import { Chip } from '@gds/Chip/Chip';
import { Typography } from '@gds/Typography/Typography';
import { IContentItemElements } from '@kontent-ai/delivery-sdk';
import { getTaxonomiesMessage } from '@page-builder/Utils/Taxonomies/GetTaxonomiesMessage';

import { ReviewCarouselTile } from '../ReviewCarouselTile/ReviewCarouselTile';

import styles from './MakeReviews.module.css';

export interface TaxonomiesProps {
  name: string;
  type: string;
  taxonomyGroup: string;
  value: {
    name: string;
    codename: string;
  }[];
}
export interface MakeReviewsProps {
  tabsTaxonomies: TaxonomiesProps;
  cars: IContentItemElements[];
  locale: Locale;
  strs: Record<string, any>;
  makeList: string[];
}

const MakeReviews: React.FC<MakeReviewsProps> = ({
  tabsTaxonomies,
  cars,
  locale,
  strs: { reviewCarouselStrs, pbReviewTaxonomiesStrs, otherStrs },
  makeList,
}) => {
  const [selectedTaxonomy, setSelectedTaxonomy] = useState('All');

  const handleChipClick = (taxonomy: string) => {
    setSelectedTaxonomy(taxonomy);
  };

  const filteredCars =
    selectedTaxonomy === 'All'
      ? cars // Show all cars when 'All' is selected
      : cars.filter(
          car =>
            car.reviews.value.length > 0 &&
            car?.reviews?.value.some((item: { name: string }) => item.name === selectedTaxonomy),
        );

  return (
    <>
      <Typography Component="h2" variant="h5" className={styles.browseBy}>
        {' '}
        {otherStrs.browseBy}
      </Typography>
      <section className={styles.chipFilters}>
        <Chip selected={selectedTaxonomy === 'All'} onClick={() => handleChipClick('All')}>
          {otherStrs.allTab}
        </Chip>
        {!!tabsTaxonomies?.value.length &&
          tabsTaxonomies.value.map(item => (
            <Chip
              selected={selectedTaxonomy === item.name}
              key={item.codename}
              onClick={() => handleChipClick(item.name)}
            >
              {getTaxonomiesMessage(item.codename, pbReviewTaxonomiesStrs)}
            </Chip>
          ))}
      </section>
      <section className={styles.makeTiles}>
        <Typography Component="h2" variant="h3">
          {' '}
          {otherStrs.allMakeCars}
        </Typography>
        <ul className={styles.cards}>
          {filteredCars.map(review => {
            const title = review?.page_builder_seo_metadata__title?.value;
            const subTitle = review?.review_quote?.value;
            if (!review.slug?.value || !review.slug || review.slug?.value === '') {
              return null;
            }
            const slug = getSlugFromReviewsAndMakes(review, makeList, locale);

            const rating = getRoundedReviewRating(review?.heycar_rating?.value);
            const images = review?.image_gallery?.value.map((item: any) => item?.url);

            const imgGallery = images.map((imgObj: string) => ({
              url: imgObj,
              isKontent: true,
              isRelative: false,
            }));
            return (
              <li key={`review-tile-${review.slug.value}`} className={styles.reviews}>
                {imgGallery && (
                  <ReviewCarouselTile
                    slug={slug}
                    imgGallery={imgGallery}
                    title={title}
                    subTitle={subTitle}
                    rating={rating}
                    strs={reviewCarouselStrs}
                    locale={locale}
                  />
                )}
              </li>
            );
          })}
        </ul>
      </section>
    </>
  );
};

export default MakeReviews;
